import { LeftOutlined } from '@ant-design/icons'
import { useQueryClient } from '@tanstack/react-query'
import {
  Button,
  Card,
  Col,
  DatePicker,
  Flex,
  Form,
  Input,
  Popconfirm,
  Row,
  Space
} from 'antd'
import { UploadFile } from 'antd/lib'
import dayjs from 'dayjs'
import { useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import {
  useCounterpartServiceGetInternalInstitutionEndpoint,
  useStocksServiceCreateInquiryEndpoint,
  useStocksServiceGetInquiryEndpoint,
  useStocksServiceGetInquiryEndpointKey,
  useStocksServiceSendInquiryEndpoint,
  useStocksServiceUpdateInquiryEndpoint,
  useUserServiceGetUserProfileEndpoint
} from 'src/api/queries'
import { ApiError, InventoryDocumentState } from 'src/api/requests'
import { requiredRules } from 'src/app-constants'
import {
  CustomItemRender,
  LoadingScreen,
  message,
  PageBreadcrumbs,
  SaveConfirm,
  Upload
} from 'src/components'
import { useFormStyles } from 'src/features/stock'
import {
  ConsumableListCreate,
  ContractSelect
} from 'src/features/stock/components/applications'
import {
  useFileHandler,
  useScrollToFirstError,
  useUser,
  ValidationError
} from 'src/hooks'
import { dayMonthYearHourMinutesFormat, yearMonthDayTimeWithT } from 'src/libs'
import { useLocales } from 'src/locales'
import { paths } from 'src/paths'
import { formErrorHandler, toDayjs } from 'src/utils'

type MedicineProps = {
  internationalNameId: string
  form: {
    id: string
  }
  quantity: string
}

export const StockApplicationsCreatePage = () => {
  const { t } = useLocales()
  const navigate = useNavigate()

  const { userData } = useUser()
  const { styles } = useFormStyles()

  const [form] = Form.useForm()
  const currentLegalEntityId = userData?.currentLegalEntityId

  const { onScrollToError } = useScrollToFirstError(form)

  const { data: userProfile } = useUserServiceGetUserProfileEndpoint()

  const { data, isLoading: isInstitutionDataLoading } =
    useCounterpartServiceGetInternalInstitutionEndpoint(
      { id: String(currentLegalEntityId) },
      [String(currentLegalEntityId)],
      { enabled: !!currentLegalEntityId }
    )

  const { id } = useParams()
  const queryClient = useQueryClient()

  const {
    existingFiles,
    setExistingFiles,
    onFileChange,
    saveFiles,
    isUploading
  } = useFileHandler({
    form,
    folder: id ? `inquiries-${id}` : 'inquiries'
  })

  const onMutationSuccess = () => {
    queryClient.invalidateQueries({
      queryKey: [useStocksServiceGetInquiryEndpointKey, String(id)]
    })
  }

  const onBackClick = () => {
    form.resetFields()
    navigate(paths.stock.applications)
  }

  const { mutate } = useStocksServiceCreateInquiryEndpoint({
    onSuccess: (data) => {
      onMutationSuccess()
      navigate(paths.stock.applicationEdit(data?.id))
      message.success(t('stock_applications.inquiry_created_success'))
    },
    onError: (error: ApiError) => formErrorHandler(error.body.errors)
  })

  const { mutateAsync: edit } = useStocksServiceUpdateInquiryEndpoint({
    onSuccess: () => {
      onMutationSuccess()
      message.success(t('common.save_success'))
    },
    onError: (error: ApiError) => formErrorHandler(error.body.errors)
  })

  const { mutateAsync: send } = useStocksServiceSendInquiryEndpoint({
    onSuccess: () => {
      onMutationSuccess()
      message.success(t('stock_applications.inquire_sent'))
      navigate(paths.stock.applicationDetails(String(id)))
    },
    onError: (error: ApiError) => formErrorHandler(error.body.errors)
  })
  const { data: inquiryData, isLoading } = useStocksServiceGetInquiryEndpoint(
    {
      id: String(id)
    },
    [String(id)],
    {
      enabled: !!id
    }
  )

  const docs = Form.useWatch('docs', form)

  const onSave = async (isSend: boolean) => {
    try {
      const values = await form.validateFields()

      const items = values?.inquiryItems?.map((item: MedicineProps) => ({
        internationalNameId: item?.internationalNameId,
        quantity: item?.quantity,
        productFormId: item?.form?.id
      }))

      const shouldSaveFiles = docs?.length !== existingFiles.length

      let mediaItems = existingFiles

      if (shouldSaveFiles) {
        const uploadedFiles = await saveFiles()
        mediaItems = uploadedFiles
      }

      const payload = {
        contractId: values?.contractId,
        documentNumber: values?.documentNumber,
        documentDate: toDayjs(values?.documentDate)?.format(
          yearMonthDayTimeWithT
        ),
        comment: values?.comment,
        items,
        mediaItems
      }

      if (id) {
        await edit({ id: String(id), requestBody: payload })

        if (isSend) {
          await send({ id: String(id) })
        }
      } else mutate({ requestBody: payload })
    } catch (error) {
      onScrollToError(error as ValidationError)
    }
  }

  useEffect(() => {
    if (id && inquiryData) {
      const existingFileIds = inquiryData?.mediaFiles
        ?.map((file) => file?.id)
        .filter(Boolean) as string[]

      setExistingFiles(existingFileIds)

      form.setFieldsValue({
        contractId: inquiryData?.supplier?.name,
        comment: inquiryData?.comment,
        documentDate: toDayjs(inquiryData?.documentDate),
        documentNumber: inquiryData?.documentNumber,
        inquiryItems: inquiryData?.items?.map((item) => {
          const prefixes = item?.internationalName?.activeSubstances?.map(
            (item) => item?.prefix
          )

          const prefixLabel = (prefixes ?? [])
            .filter((p) => p?.trim() !== '')
            .join('+')

          return {
            name: `${item?.internationalName?.name}${prefixLabel ? ` (${prefixLabel})` : ''} ${item?.internationalName?.dosage}`,
            internationalNameId: item?.internationalName?.id,
            form: {
              name: item?.productForm?.name,
              id: item?.productForm?.id
            },
            quantity: item?.quantity,
            items: item?.beneficiaryStocks || []
          }
        }),

        docs: existingFileIds.map((id) => ({
          uid: id,
          name: `File-${id}`,
          status: 'done'
        }))
      })
    }
  }, [inquiryData, id])

  if (isLoading || isInstitutionDataLoading) {
    return <LoadingScreen />
  }

  const pageTitle = t(
    `stock_applications.${id ? 'edit_application' : 'create_new_application'}`
  )

  const isNew = inquiryData?.state === InventoryDocumentState.NEW

  const isCreateOrNew = !inquiryData || (inquiryData && isNew)

  return (
    <PageBreadcrumbs
      title={pageTitle}
      items={[
        { title: t('menu.consumable_medicaments') },
        { title: t('stock.applications') },
        {
          title: pageTitle
        }
      ]}
    >
      <Flex vertical gap={24} className={styles.form}>
        <Flex justify="space-between">
          <Button
            icon={<LeftOutlined />}
            className="fit-content"
            onClick={onBackClick}
          >
            {t('common.back')}
          </Button>

          <Space size={8}>
            <Button onClick={onBackClick}>{t('common.cancel')}</Button>

            {isCreateOrNew && (
              <SaveConfirm
                title={`${t('stock_applications.save_as_draft')}?`}
                placement="leftBottom"
                onConfirm={() => onSave(false)}
                okButtonProps={{ loading: isUploading || isLoading }}
              >
                <Button id="create-inquiry" form="create-inquiry">
                  {t('stock_applications.save_as_draft')}
                </Button>
              </SaveConfirm>
            )}
            {isNew && (
              <Popconfirm
                placement="leftBottom"
                description={t('stock_applications.confirm_send_desc')}
                title={t('stock_applications.confirm_send_title')}
                onConfirm={() => onSave(true)}
              >
                <Button type="primary">{t('stock_applications.send')}</Button>
              </Popconfirm>
            )}
          </Space>
        </Flex>

        <Form
          layout="vertical"
          name="create-inquiry"
          form={form}
          onFinish={() => onSave(false)}
          disabled={!isCreateOrNew}
          initialValues={{ documentDate: dayjs() }}
          scrollToFirstError
        >
          <Flex vertical gap={16}>
            <Card>
              <Form.Item shouldUpdate>
                {({ getFieldValue }) => {
                  const isContractDisabled = !getFieldValue('documentDate')

                  return (
                    <Row gutter={[24, 16]}>
                      <Col span={24}>
                        <Form.Item
                          name="documentDate"
                          label={t(`stock_applications.invoice_date`)}
                          rules={requiredRules}
                        >
                          <DatePicker
                            className="full-width"
                            format={dayMonthYearHourMinutesFormat}
                            disabled={!!id}
                            showTime
                          />
                        </Form.Item>
                      </Col>

                      <Col span={24}>
                        <Form.Item
                          name="applicant"
                          label={t(`stock_applications.applicant`)}
                        >
                          <Input
                            className="full-width"
                            disabled
                            placeholder={`${data ? `${data.name} - ` : ''}  ${userProfile?.firstName} ${userProfile?.lastName}`}
                          />
                        </Form.Item>
                      </Col>

                      <Col span={24}>
                        <Form.Item
                          name="contractId"
                          label={t(`counterparts.provider`)}
                          rules={requiredRules}
                        >
                          <ContractSelect
                            form={form}
                            activeDate={form.getFieldValue('documentDate')}
                            disabled={isContractDisabled || !!id}
                            placeholder={t(`counterparts.provider`)}
                          />
                        </Form.Item>
                      </Col>
                    </Row>
                  )
                }}
              </Form.Item>
            </Card>

            <Card>
              <Form.Item name="inquiryItems" rules={requiredRules}>
                <ConsumableListCreate form={form} isFinished={!isCreateOrNew} />
              </Form.Item>
            </Card>

            <Card>
              <Flex gap={24} vertical>
                <Card title={t('stock_applications.uploaded_documents')}>
                  <Form.Item name="docs" className="full-width">
                    <div id="docs-wrapper">
                      <Upload
                        defaultFileList={
                          inquiryData?.mediaFiles?.map((file) => ({
                            ...file,
                            uid: file.id,
                            name: file.name
                          })) as UploadFile[]
                        }
                        onChange={onFileChange}
                        customRequest={() => null}
                        itemRender={(_, file, __, { remove }) => (
                          <CustomItemRender file={file} onRemove={remove} />
                        )}
                      />
                    </div>
                  </Form.Item>
                </Card>

                <Card title={t('common.comment')}>
                  <Form.Item name="comment">
                    <Input.TextArea
                      disabled={!!id}
                      rows={5}
                      placeholder={t('common.leave_comment')}
                    />
                  </Form.Item>
                </Card>
              </Flex>
            </Card>
          </Flex>
        </Form>
      </Flex>
    </PageBreadcrumbs>
  )
}
